import Vue, { CreateElement } from 'src/vue'
import { Component } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import { Input } from '@src/elements/input/input'
import { Button } from '@src/elements/button/button'
import { Form } from '@src/components/form/form'
import { Option } from '@src/elements/select/option'
import { Select } from '@src/elements/select/select'
import { ContentBox } from '@src/elements/content-box/content-box'

import Club from '@src/data/club'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { capitalizeFirstLetter, snakecaseKeys, track } from '@src/util/functions'
import { ClubGeneralSettingsDTO } from '@src/dto/club-dto'

import './general-settings.sass'
import { WeightUnit } from '@src/util/weight'
import { EmojiSelectOption } from '@src/routes/club/emoji'
import { EmojiType } from '@src/routes/club/emoji/types/EmojiType'
import EmojiData from '@src/routes/club/emoji/types/EmojiData'
import { EmojiService } from '@src/service/emoji'

@Component({
  computed: {
    club () {
      return this.$store.state.club
    },
    emojiActiveType () {
      return this.$store.state.emojiActiveType
    },
    emojiData () {
      return this.$store.state.emojiData
    }
  }
})
export class GeneralSettings extends Vue {

  private club: Club
  private widgetLayouts: string[] = ['smallPT', 'mediumPT']
  private weightUnits: WeightUnit[] = [WeightUnit.KG, WeightUnit.LBS]
  private emojiActiveType: EmojiType
  private emojiData: EmojiData

  private apiClientClub: ApiClient
  private errors: any = {}

  constructor () {
    super()
    this.apiClientClub = new ApiClient()
    track('/club/general-settings')
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MY_CLUB')} - ${this.$root.$t('PAGE.CLUB.GENERAL_SETTINGS.TAB_NAME')}`
  }

  save () {
    this.apiClientClub.put(API.TRAINERS_API.CLUB, { club: snakecaseKeys(new ClubGeneralSettingsDTO(this.club)) })
      .then(() => {
        this.errors = {}
        this.$store.dispatch('showToast', { message: this.$t('ACKNOWLEDGEMENT.SUCCESSFUL_CHANGES'), fadeOut: true })
      })
      .catch((error) => this.errors = camelcaseKeys(error.errorData.errors))
  }

  render (h: CreateElement) {
    let units = this.weightUnits.map(unit => {
      return <Option type='single-option' value={unit} label={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.MEASUREMENT_UNIT_${unit.toUpperCase()}`)} selected={this.club.weightUnit === unit} />
    })
    let widgetLayouts = this.widgetLayouts.map(layout => {
      return <Option type='single-option' value={layout} label={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.${layout.toUpperCase()}`)} selected={this.club.stationLayout === layout} />
    })
    const currentEmoji = this.emojiData[this.emojiActiveType].selected || this.emojiData[this.emojiActiveType].available[0]
    const emojiSelectValue = currentEmoji ? `${capitalizeFirstLetter(this.emojiActiveType)}-${currentEmoji!.name}` : capitalizeFirstLetter(this.emojiActiveType)
    return <div class='general-settings'>
      <ContentBox>
        <Form direction={'column'} slot={'content'}>
          <div>
            <Select label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.MEASUREMENT_UNIT')} ref='weight-unit'
                    value={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.MEASUREMENT_UNIT_${this.club.weightUnit.toUpperCase()}`)}
                    valueChanged={(value) => this.club.weightUnit = value} class={`weight-unit-select ${this.errors.weightUnit ? 'error' : ''}`}>{units}</Select>
            <Input type='number' label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.EXERCISE_PREPARATION')} ref='countdown-len' min={0}
                   value={this.club.screenCountdownLen.toString()}
                   valueChanged={(value) => this.club.screenCountdownLen = value}
                   class={this.errors.screenCountdownLen ? 'error' : ''} />
          </div>
          <div>
            <Input type='number' label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.EXERCISE_EXECUTION')} ref='exercise-len' min={0} value={this.club.screenExerciseLen.toString()}
                   valueChanged={(value) => this.club.screenExerciseLen = value}
                   class={this.errors.screenExerciseLen ? 'error' : ''} />
            <Input type='number' label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.RESULTS_SCREEN')} ref='summary-len' min={0} value={this.club.screenSummaryLen.toString()}
                   valueChanged={(value) => this.club.screenSummaryLen = value}
                   class={this.errors.screenSummaryLen ? 'error' : ''} />
          </div>
          <div>
            <Select label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.STATION_WIDGETS_LAYOUT')} class={`layout-select ${this.errors.stationLayout ? 'error' : ''}`}
                    valueChanged={(value: string) => this.club.stationLayout = value}
                    value={this.club.stationLayout ? this.$t(`PAGE.CLUB.GENERAL_SETTINGS.${this.club.stationLayout.toUpperCase()}`) : ''}>{widgetLayouts}</Select>
            <Select label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.OPTION')} class='layout-select'
                    valueChanged={(value) => this.club.displayPixScore = value === 'show'}
                    value={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.${this.club.displayPixScore ? 'SHOW' : 'HIDE'}`)}>
               <Option type='single-option' value='show' label={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.SHOW`)} selected={!this.club.displayPixScore} />
               <Option type='single-option' value='hide' label={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.HIDE`)} selected={this.club.displayPixScore} />
            </Select>
          </div>
          <div>

            <Select
              expandWidthWhenActive={true}
              label='Emojis'
              class='layout-select emoji-select'
              value={emojiSelectValue}>
                 <EmojiSelectOption />
            </Select>
          </div>
          <br />
        </Form>
        <div slot={'bottom'}>
          <Button type='elevated' class='apply-btn' label={this.$t('APPLY')} click={this.save} />
        </div>
      </ContentBox>
    </div>
  }

  beforeDestroy () {
    this.$store.dispatch('closeToast')
    return new EmojiService().getEmojis().then(response => {
      this.$store.dispatch('updateEmojiData', response)
      Object.keys(response).forEach((key) => {
        if (response[key].selected) {
          this.$store.dispatch('updateEmojiActiveType', key)
          return
        }
      })
    })

  }
}
